// API v1 Client for Live 2.0
// Handles authentication and job management for API v1 endpoints

export interface JobStatus {
  job_id: string
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled'
  progress: number  // 0-100
  result_url?: string | null
  error?: string | null
  created_at: string  // ISO format datetime
  completed_at?: string | null  // ISO format datetime
}

export interface SubscriptionResponse {
  subscription_id: string
  tier: string
  status: string
  current_period_end?: string
}

export interface UsageResponse {
  current_period_start: string
  current_period_end: string
  jobs_used: number
  jobs_limit: number
  compute_hours_used: number
  compute_hours_limit: number
  current_month?: {
    reactions?: number
    reactions_quota?: number
    api_calls?: number
    api_calls_quota?: number
    percentage_used?: number
    jobs_used?: number
    jobs_limit?: number
    compute_hours_used?: number
    compute_hours_limit?: number
  }
}

export class APIv1Client {
  private baseUrl: string
  private apiKey: string | null = null

  constructor(baseUrl?: string, apiKey?: string) {
    // Use environment variable or relative path for production
    // In production, API is on the same domain, so we use relative paths
    this.baseUrl = baseUrl || import.meta.env.VITE_API_BASE || ''
    if (apiKey) {
      this.apiKey = apiKey
    }
  }

  setApiKey(apiKey: string): void {
    this.apiKey = apiKey
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    }

    if (this.apiKey) {
      headers['X-API-Key'] = this.apiKey
    }

    const response = await fetch(url, {
      ...options,
      headers,
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ 
        detail: response.statusText 
      }))
      throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`)
    }

    return response.json()
  }

  async listJobs(limit: number = 20): Promise<JobStatus[]> {
    return this.request<JobStatus[]>(`/api/v1/jobs?limit=${limit}`)
  }

  async getJobStatus(jobId: string): Promise<JobStatus> {
    return this.request<JobStatus>(`/api/v1/jobs/${jobId}`)
  }

  async downloadResult(url: string, filename: string): Promise<void> {
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: this.apiKey ? { 'X-API-Key': this.apiKey } : {},
      })

      if (!response.ok) {
        throw new Error(`Failed to download: ${response.statusText}`)
      }

      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Failed to download result')
    }
  }

  async getSubscription(): Promise<SubscriptionResponse> {
    return this.request<SubscriptionResponse>('/api/v1/billing/subscription')
  }

  async getUsage(): Promise<UsageResponse> {
    return this.request<UsageResponse>('/api/v1/billing/usage')
  }

  async createCheckoutSession(tier: string, successUrl: string, cancelUrl: string): Promise<{ url: string }> {
    return this.request<{ url: string }>('/api/v1/billing/checkout/session', {
      method: 'POST',
      body: JSON.stringify({ tier, success_url: successUrl, cancel_url: cancelUrl }),
    })
  }

  async createPortalSession(returnUrl: string): Promise<{ url: string }> {
    return this.request<{ url: string }>('/api/v1/billing/portal', {
      method: 'POST',
      body: JSON.stringify({ return_url: returnUrl }),
    })
  }
}

